/**
 * Is the Velora desktop app on this PC? Three answers:
 *
 * - `in-app`: this page runs inside the app (its bridge is present);
 * - `installed`: Velora runs in a browser, and the app answered its
 *   read-only loopback status (apps/desktop src/core/presence.ts), so the
 *   page can offer "Open in the Velora app" (a velora:// link);
 * - `missing`: no answer (not installed, not running, or the browser keeps
 *   the page from the loopback address): offer "Get the Velora app".
 *
 * The app answers only Velora's own origin and only with its name and
 * version, and (0.4.4+) whether its call pops are on (`callPops`: on, off or
 * signed_out; older apps send nothing, read as null). Probed once per page
 * load and when the window regains focus.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { loopbackInit, type FetchLike } from '../../modules/imaging/capture/helper.js';
import { desktopBridge } from './bridge.js';

export const PRESENCE_PORTS = [17473, 17474] as const;
export const PRESENCE_PATH = '/velora-app/v1/status';
const TIMEOUT_MS = 1200;

export type DesktopCallPops = 'on' | 'off' | 'signed_out';

export type DesktopAppState =
  | { state: 'checking' }
  | { state: 'in-app'; version: string | null }
  | { state: 'installed'; version: string; callPops: DesktopCallPops | null }
  | { state: 'missing' };

export function parsePresence(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (b['app'] !== 'Velora Dental' || typeof b['version'] !== 'string') return null;
  return /^\d+\.\d+\.\d+$/.test(b['version']) ? b['version'] : null;
}

/** The app's call-pop state (0.4.4+), or null when it does not say. */
export function parseCallPops(body: unknown): DesktopCallPops | null {
  if (!body || typeof body !== 'object') return null;
  const v = (body as Record<string, unknown>)['callPops'];
  return v === 'on' || v === 'off' || v === 'signed_out' ? v : null;
}

export async function probeDesktopApp(
  fetchImpl: FetchLike = (u, i) => fetch(u, i),
  timeoutMs = TIMEOUT_MS,
): Promise<DesktopAppState> {
  const bridge = desktopBridge();
  if (bridge) return { state: 'in-app', version: bridge.version ?? null };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const found = await Promise.any(
      PRESENCE_PORTS.map(async (port) => {
        const res = await fetchImpl(
          `http://127.0.0.1:${port}${PRESENCE_PATH}`,
          loopbackInit(ctrl.signal),
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
        const body: unknown = await res.json();
        const v = parsePresence(body);
        if (!v) throw new Error('not the Velora app');
        return { version: v, callPops: parseCallPops(body) };
      }),
    );
    return { state: 'installed', ...found };
  } catch {
    return { state: 'missing' };
  } finally {
    clearTimeout(timer);
    ctrl.abort();
  }
}

let current: DesktopAppState = { state: 'checking' };
let started = false;
const listeners = new Set<() => void>();

function set(next: DesktopAppState): void {
  current = next;
  for (const l of listeners) l();
}

function refresh(): void {
  void probeDesktopApp().then(set);
}

export function useDesktopApp(): DesktopAppState {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => current,
  );
  useEffect(() => {
    if (started) return;
    started = true;
    refresh();
    window.addEventListener('focus', refresh);
  }, []);
  return s;
}

/** Deep link that opens the app on the same page (the app allows only its own routes). */
export const OPEN_IN_APP_HREF = 'velora://home';
